import {CurrencyPipe} from '@angular/common';
import {Component, Inject, LOCALE_ID} from '@angular/core';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {EmTranslatePipe, ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES,
  ProviderId,
  Rider,
  RiderType,
} from '@emma-labs/shared/typescript';
import {UntilDestroy} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TableModule, TableRowSelectEvent} from 'primeng/table';
import {finalize} from 'rxjs';
import {ProtectionService} from '../../../../../services/protection.service';
import {CoverageCardComponent} from '../../coverage-card/coverage-card.component';
import {BaseRiderCardComponent} from '../base-rider-card.component';

@UntilDestroy()
@Component({
  selector: 'em-child-rider-card',
  templateUrl: './child-rider-card.component.html',
  imports: [
    CardModule,
    CoverageCardComponent,
    ReactiveFormsModule,
    TableModule,
    ProgressSpinnerModule,
    CurrencyPipe,
    EmTranslatePipe,
  ],
})
export class ChildRiderCardComponent extends BaseRiderCardComponent {
  private cachedRiders: Rider[] | undefined;

  constructor(
    override readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly translate: TranslationService,
    private readonly protectionService: ProtectionService,
    @Inject(LOCALE_ID) locale: string,
  ) {
    super(fb, locale);
  }

  onSelect($event: TableRowSelectEvent): void {
    this.coverageSelected.emit({amount: +$event.data.value, type: RiderType.CHILD});
  }

  setProducts(): void {
    if (!this._selectedLifeInsuranceCoverageAmount) {
      return;
    }

    const coverageAmounts = HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES.filter(
      (value) => this._selectedLifeInsuranceCoverageAmount! >= value * 2,
    );

    const riderConfigs = coverageAmounts.map((coverageAmount) => ({
      type: RiderType.CHILD,
      coverage: coverageAmount,
    }));

    // No need to recalculate prices since they remain constant
    if (
      this.cachedRiders &&
      coverageAmounts.every((amount) => this.cachedRiders?.some((rider) => rider.coverage === amount))
    ) {
      const riders = this.cachedRiders.filter((rider) => coverageAmounts.includes(rider.coverage));
      this.updateProductList(riders);
      this.isPriceLoading = false;
      return;
    }

    if (!riderConfigs.length) {
      this.products = [{name: {fr: 'Aucun', en: 'None'}, value: '0'}];
      return;
    }

    this.protectionService
      .getRiders(this._ratesVersion, ProviderId.HUMANIA_EMMA_V2, riderConfigs)
      .pipe(finalize(() => (this.isPriceLoading = false)))
      .subscribe({
        next: (riders) => {
          this.cachedRiders = riders;
          this.updateProductList(riders);
        },
        error: (_) => {
          this.toastService.error(
            this.translate.currentLanguageIsFr()
              ? `Une erreur est survenue lors du chargement de l'avenant enfant. Veuillez réessayer`
              : `An error occurred while loading the child rider. Please try again`,
          );
        },
      });
  }

  private updateProductList(riders: Rider[]): void {
    this.selectedProduct = {name: {fr: 'Aucun', en: 'None'}, value: '0'};

    this.products = [
      {name: {fr: 'Aucun', en: 'None'}, value: '0'},
      ...riders.map((rider) => {
        const coverage = this.currencyPipe.transform(rider.coverage, 'CAD', 'symbol-narrow', '1.0-0');
        const product = {
          name: {
            en: `${coverage} coverage`,
            fr: `${coverage} de couverture`,
          },
          value: rider.coverage.toString(),
          price: rider.price,
        };

        if (this.form.controls.coverageAmount.value === rider.coverage) {
          this.selectedProduct = product;
        }

        return product;
      }),
    ];
  }
}
